import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TTYD_3 } from '../constants/Constantsi18';
import { useNewChatTrigger } from './useNewChatTrigger';
import { buildUserProfileDetails } from '../utils/userProfileDetails';

export const useChat = () => {
  const navigate = useNavigate();
  const { registerTrigger, registerReloadTrigger } = useNewChatTrigger();
  const { userInfo, activeKnowledgeId, activeApp, isThinkingModeEnabled, apiVersionName } = useSelector((state: any) => ({
    userInfo: state.loginResource,
    activeKnowledgeId: state.loginResource.activeKnowledgeId,
    activeApp: state.loginResource.activeApp,
    isThinkingModeEnabled: state.loginResource.isThinkingModeEnabled ?? false,
    apiVersionName: state.loginResource.apiVersionName ?? 'V1',
  }));
  
  const [searchParams] = useSearchParams();
  const chatConversation = searchParams.get('conversationId');
  const [showChat, setShowChat] = useState(false);
  const [forceRenderKey, setForceRenderKey] = useState(0);
  const [conversationTransitionKey, setConversationTransitionKey] = useState(0);
  const [previousConversationId, setPreviousConversationId] = useState<string | null>(null);

  // Helper function to get team name
  const getTeamName = () => {
    try {
      if (!userInfo?.company_data || !userInfo?.preferredCompany || !userInfo?.preferredProject) {
        return TTYD_3;
      }

      // Find the preferred company
      const company = userInfo.company_data.find((company: any) => 
        company.companyId === userInfo.preferredCompany
      );
      
      if (!company) {
        return TTYD_3;
      }

      // Get the project identifier (handle both array and single value cases)
      const projectIdentifier = Array.isArray(userInfo.preferredProject) 
        ? userInfo.preferredProject[0]?.project_name || userInfo.preferredProject[0]
        : userInfo.preferredProject;



      // Find the project in the company's project list
      const project = company.projectList?.find((project: any) => 
        project.project_id === projectIdentifier || 
        project.project_name === projectIdentifier
      );

      if (!project) {
        return TTYD_3;
      }

      // Return the team name (assuming it's project.project_name or project.team_name)
      const teamName = project.team_name || project.project_name || project.project_id;

      return teamName;
      
    } catch (error) {

      return TTYD_3;
    }
  };

  const userProfileDetails = useMemo(() => buildUserProfileDetails(userInfo), [
    userInfo?.userPortfolios,
    userInfo?.preferredProject,
    userInfo?.selectedRole,
    userInfo?.selectedBrands,
    userInfo?.selectedFunctions,
    userInfo?.selectedGoals,
  ]);

  // Track conversation transitions to force component remount when switching between conversation types
  useEffect(() => {
    // Check if we're transitioning between different conversation states
    const isTransition = (
      // From specific conversation to new chat (null)
      (previousConversationId !== null && chatConversation === null) ||
      // From new chat (null) to specific conversation  
      (previousConversationId === null && chatConversation !== null) ||
      // From one specific conversation to another
      (previousConversationId !== null && chatConversation !== null && previousConversationId !== chatConversation)
    );

    if (isTransition) {
      // Increment transition key to force complete component remount
      setConversationTransitionKey(prev => prev + 1);
    }

    // Update previous conversation ID
    setPreviousConversationId(chatConversation);
  }, [chatConversation, previousConversationId]);

  const handleChat = (conversationId: string | null) => {
    setShowChat(true);
  };

  // Handle new chat navigation - clear conversationId parameter
  const handleNewChat = () => {
    // Navigate to /chat without any query parameters to start fresh
    navigate('/chat', { replace: true });
  };

  // Force component rerender by updating key
  const forceNewChat = () => {
    // Navigate to /chat without conversationId
    navigate('/chat', { replace: false });
    // Force rerender by updating both keys to ensure complete remount
    setForceRenderKey(prev => prev + 1);
    setConversationTransitionKey(prev => prev + 1);
  };

  // Force component rerender without navigation (for rename)
  const forceReload = () => {
    // Just force rerender by updating the key, no navigation
    setForceRenderKey(prev => prev + 1);
  };

  // Handle chat deletion from within the Chat component
  const handleChatDelete = (conversationId: string) => {
    // Navigate to new chat route (without conversationId)
    navigate('/chat', { replace: true });
    setForceRenderKey(prev => prev + 1);
    setConversationTransitionKey(prev => prev + 1);
  };

  // Register this component's forceNewChat with the global trigger
  useEffect(() => {
    const unregisterNewChat = registerTrigger(forceNewChat);
    const unregisterReload = registerReloadTrigger(forceReload);
    
    return () => {
      unregisterNewChat();
      unregisterReload();
    };
  }, [registerTrigger, registerReloadTrigger]);

  // Create a unique key that changes when we want to force rerender
  // Include conversationTransitionKey to ensure complete remount when switching between conversation types
  // Include activeApp and activeKnowledgeId to ensure remount when these values change
  const chatKey = `${userInfo?.preferredProject}-${chatConversation || 'new'}-${forceRenderKey}-${conversationTransitionKey}-${(activeApp && Object.keys(activeApp).length > 0 && activeApp?.app_display_name) && activeApp?.app_display_name || 'no-app'}-${activeKnowledgeId || 'no-knowledge'}`;

  return {
    userInfo,
    chatConversation,
    showChat,
    setShowChat,
    getTeamName,
    handleChat,
    handleNewChat,
    forceNewChat,
    handleChatDelete,
    chatKey,
    activeKnowledgeId,
    activeApp,
    isThinkingModeEnabled,
    apiVersionName,
    userProfileDetails
  };
};
